import React, { useState } from 'react'
import { Head, router, Link } from '@inertiajs/react'
import DashboardLayout from '../layouts/DashboardLayout'
import { ShieldCheck, Stethoscope, PhoneOff, Star } from 'lucide-react'

// The practice manager's PRIVATE feedback tab. Server-side this page 404s for anyone who is not on
// PRACTICE_MANAGER_EMAILS, and the nav item is not rendered for them either.
//
// The summary strip is at the top on purpose: the product is the PATTERN, not the individual message.

const RATING_STYLE = (r) =>
  r == null ? 'bg-slate-100 text-slate-500'
    : r <= 2 ? 'bg-rose-100 text-rose-700'
    : r === 3 ? 'bg-amber-100 text-amber-700'
    : 'bg-emerald-100 text-emerald-700'

const STATE_TABS = [
  { key: '', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'actioned', label: 'Actioned' },
  { key: 'closed', label: 'Closed' },
]

export default function Feedback({ entries = [], summary = {}, filters = {}, themes = [], providers = [] }) {
  return (
    <DashboardLayout>
      <Head title="Feedback" />

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-brand-ink">Patient feedback</h1>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-brand-muted">
            <ShieldCheck className="h-4 w-4" />
            Private to you. Reception and the clinical team cannot open this page.
          </p>
        </div>
      </div>

      {/* PATTERNS FIRST */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat label={`Asked (${summary.month || ''})`} value={summary.sent ?? 0}
              sub={summary.provider_name || undefined} />
        <Stat label="Answered" value={`${summary.responses ?? 0}`} sub={`${summary.response_rate ?? 0}%`} />
        <Stat label="Average" value={summary.average_rating ? `${summary.average_rating}/5` : '—'} />
        <Stat label="Red, still open" value={summary.red_open ?? 0} tone={summary.red_open > 0 ? 'danger' : null} />
        <Stat label="To review" value={summary.pending_review ?? 0} />
      </div>

      {summary.top_themes?.length > 0 && (
        <div className="mb-6 rounded-2xl border border-brand-line bg-white p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-muted">What keeps coming up</p>
          <div className="flex flex-wrap gap-2">
            {summary.top_themes.map((t) => (
              <Link key={t.theme} href={`/feedback?theme=${t.theme}`}
                    className="rounded-full bg-brand-surface px-3 py-1.5 text-sm text-brand-ink hover:bg-brand-primary/10">
                {t.theme.replace(/_/g, ' ')} <span className="ml-1 font-semibold">{t.count}</span>
              </Link>
            ))}
            {(filters.theme || filters.only || filters.state) && (
              <Link href="/feedback" className="rounded-full px-3 py-1.5 text-sm text-brand-muted underline">Clear filters</Link>
            )}
          </div>
        </div>
      )}

      {/* Per-dentist view. Dr Eliska runs ~3x Dr Chalita's volume, so pooled counts hide a
          dentist-specific pattern. Chairside themes (pace, comfort, plan clarity) belong to whoever
          was in the room; parking/reception/access are practice-wide. */}
      {providers.length > 1 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-brand-muted">Dentist</span>
          <Link href="/feedback"
                className={`rounded-xl px-3 py-1.5 text-sm ${
                  !filters.provider ? 'bg-brand-primary text-white' : 'bg-white text-brand-muted hover:bg-brand-surface'
                }`}>
            Everyone
          </Link>
          {providers.map((p) => (
            <Link key={p.id} href={`/feedback?provider=${p.id}`}
                  className={`rounded-xl px-3 py-1.5 text-sm ${
                    String(filters.provider) === String(p.id)
                      ? 'bg-brand-primary text-white' : 'bg-white text-brand-muted hover:bg-brand-surface'
                  }`}>
              {p.name} <span className="ml-1 opacity-70 tabular-nums">{p.count}</span>
            </Link>
          ))}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {STATE_TABS.map((t) => (
          <Link key={t.key} href={t.key ? `/feedback?state=${t.key}` : '/feedback'}
                className={`rounded-xl px-3 py-1.5 text-sm ${
                  (filters.state || '') === t.key ? 'bg-brand-primary text-white' : 'bg-white text-brand-muted hover:bg-brand-surface'
                }`}>
            {t.label}
          </Link>
        ))}
        <Link href="/feedback?only=red"
              className={`rounded-xl px-3 py-1.5 text-sm ${
                filters.only === 'red' ? 'bg-brand-danger text-white' : 'bg-white text-brand-danger hover:bg-brand-danger/10'
              }`}>
          Needs me
        </Link>
      </div>

      {entries.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-brand-line bg-white py-16 text-center text-brand-muted">
          Nothing here yet. Forms answered by patients will appear as soon as they come in.
        </div>
      ) : (
        <div className="space-y-3">{entries.map((e) => <Entry key={e.id} e={e} themes={themes} />)}</div>
      )}
    </DashboardLayout>
  )
}

function Stat({ label, value, sub, tone }) {
  return (
    <div className={`rounded-2xl border bg-white p-4 ${tone === 'danger' ? 'border-brand-danger/40' : 'border-brand-line'}`}>
      <p className="text-xs uppercase tracking-wider text-brand-muted">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${tone === 'danger' ? 'text-brand-danger' : 'text-brand-ink'}`}>{value}</p>
      {sub && <p className="text-xs text-brand-muted">{sub}</p>}
    </div>
  )
}

function Entry({ e, themes }) {
  const [note, setNote] = useState(e.manager_note || '')
  const [open, setOpen] = useState(false)
  const [requesting, setRequesting] = useState(false)

  const setState = (review_state) => router.patch(`/feedback/${e.id}`, { review_state }, { preserveScroll: true })
  const saveNote = () => router.patch(`/feedback/${e.id}`, { manager_note: note }, { preserveScroll: true })
  // 🚨 NEVER gate this on e.rating. Asking only happy patients for a public review is "review
  // gating" — against Google's policy (profile suspension) and HPCSA Booklet 9. The button is on
  // every entry at every score; when to press it is the manager's judgement, same as the
  // appointment modal. visit_feedback_audit.rb fails the build if a rating condition appears here.
  const askReview = () => {
    if (requesting || e.review_requested) return // never fire twice (also guarded on the server)
    if (!window.confirm('Send this patient a Google review request on WhatsApp?')) return
    setRequesting(true)
    router.patch(`/feedback/${e.id}/request_review`, {}, { preserveScroll: true, onFinish: () => setRequesting(false) })
  }

  return (
    <div className={`rounded-2xl border bg-white p-4 ${e.clinical_concern ? 'border-brand-danger/50' : 'border-brand-line'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-lg px-2 py-0.5 text-sm font-semibold ${RATING_STYLE(e.rating)}`}>
              {e.rating ? `${e.rating}/5` : '—'}
            </span>
            <span className="font-medium text-brand-ink">{e.name}</span>
            {e.phone && <a href={`tel:${e.phone}`} className="text-sm text-brand-primary hover:underline">{e.phone}</a>}
            {e.anonymous && <Badge>anonymous</Badge>}
            {e.context === 'cancellation' && <Badge tone="amber">cancelled</Badge>}
            {e.clinical_concern && (
              <span className="inline-flex items-center gap-1 rounded-lg bg-brand-danger/10 px-2 py-0.5 text-xs font-semibold text-brand-danger">
                <Stethoscope className="h-3 w-3" /> health concern — Dr Chalita
              </span>
            )}
            {!e.contact_ok && (
              <span className="inline-flex items-center gap-1 text-xs text-brand-muted">
                <PhoneOff className="h-3 w-3" /> do not contact
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-brand-muted">
            {e.visit_date}{e.provider ? ` · ${e.provider}` : ''} · answered {e.submitted_at}
          </p>
        </div>
        <div className="flex shrink-0 gap-1.5">
          {['new', 'actioned', 'closed'].map((s) => (
            <button key={s} onClick={() => setState(s)}
                    className={`rounded-lg px-2.5 py-1 text-xs capitalize ${
                      e.review_state === s ? 'bg-brand-primary text-white' : 'bg-brand-surface text-brand-muted hover:bg-brand-primary/10'
                    }`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {e.probe && (
        <div className="mt-3 rounded-xl bg-brand-surface/60 p-3">
          <p className="text-xs text-brand-muted">{e.probe}</p>
          <p className="mt-1 text-sm text-brand-ink">{e.probe_answer || <em className="text-brand-muted">no answer</em>}</p>
        </div>
      )}

      {e.comment && <p className="mt-3 whitespace-pre-wrap text-sm text-brand-ink">{e.comment}</p>}

      {e.themes?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {e.themes.map((t) => (
            <span key={t} className="rounded-full bg-brand-surface px-2 py-0.5 text-[11px] text-brand-muted">
              {t.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}

      <div className="mt-3 border-t border-brand-line pt-3">
        {open ? (
          <div className="flex gap-2">
            <input value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="What did you do about it?"
                   className="flex-1 rounded-lg border border-brand-line px-3 py-1.5 text-sm" />
            <button onClick={saveNote} className="rounded-lg bg-brand-primary px-3 py-1.5 text-sm text-white">Save</button>
          </div>
        ) : (
          <button onClick={() => setOpen(true)} className="text-xs text-brand-muted hover:text-brand-ink">
            {e.manager_note ? `📝 ${e.manager_note}` : '+ Add a note on what you did'}
          </button>
        )}

        {e.can_request_review && (
          e.review_requested ? (
            // Already asked — greyed, disabled, non-clickable so we can never ask the same patient twice.
            <button
              type="button"
              disabled
              title="A Google review request has already been sent to this patient"
              className="mt-2 flex cursor-not-allowed items-center gap-1.5 text-xs text-brand-muted opacity-60"
            >
              <Star className="h-3.5 w-3.5" /> Review requested
            </button>
          ) : (
            <button
              onClick={askReview}
              disabled={requesting}
              className={`mt-2 flex items-center gap-1.5 text-xs text-amber-700 ${requesting ? 'cursor-wait opacity-50' : 'hover:underline'}`}
            >
              <Star className="h-3.5 w-3.5" /> {requesting ? 'Sending…' : 'Ask for a Google review'}
            </button>
          )
        )}

      </div>
    </div>
  )
}

function Badge({ children, tone }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] ${
      tone === 'amber' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
    }`}>{children}</span>
  )
}
